import _ from "lodash";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";
import client from "lib/apolloClient/client";
import {
  createNewCommentTemplateMutation,
  updateCommentTemplateMutation,
  deleteCommentTemplateMutation,
  publishCommentBankMutation,
} from "./Mutation";
import { getAllCommentTemplatesQuery } from "./Queries";
import { getAIResponse } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { USER_TYPES } from "ProgressReportSetup/routes/CommentTemplates/modules/Utils";
import {
  createAIVoiceToneMutation,
  updateAIVoiceToneMutation,
  deleteAIVoiceToneMutation,
} from "ProgressReportCommon/modules/ProgressReportMutations.js";
import {
  getAIVoiceTonesForReportsFromCache,
  writeAIVoiceTonesForReportsToCache,
} from "ProgressReportCommon/modules/ProgressReportGraphqlHelpers";
import { dispatchError } from "Utils";

export const NAME = "commentTemplates";

const SET_TEMPLATE_DATA = "SET_TEMPLATE_DATA" + " " + NAME;

export const setTemplateData = data => {
  return {
    type: SET_TEMPLATE_DATA,
    payload: data,
  };
};

export const createProgressReportCommentTemplate = ({ input, queryInput }) => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: createNewCommentTemplateMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAllCommentTemplatesQuery,
            variables: { filters: queryInput },
          },
        ],
      });
      if (_.isEmpty(response)) return false;
      else return response;
    } catch (error) {
      console.error(error);
    }
  };
};

export const updateProgressReportCommentTemplate = ({ input, queryInput }) => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: updateCommentTemplateMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAllCommentTemplatesQuery,
            variables: { filters: queryInput },
          },
        ],
      });
      if (_.isEmpty(response)) return false;
      else return response;
    } catch (e) {
      console.error(e);
    }
  };
};

export const deleteProgressReportCommentTemplate = ({ input, queryInput }) => {
  return async (dispatch, getState) => {
    try {
      const response = await client.mutate({
        mutation: deleteCommentTemplateMutation,
        variables: {
          input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getAllCommentTemplatesQuery,
            variables: { filters: queryInput },
          },
        ],
      });
      if (_.isEmpty(response)) return false;
      else return response;
    } catch (e) {
      console.error(e);
    }
  };
};

export const publishCommentBank = () => {
  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;
    const shouldUpdateComments = ACLStore.can(
      "FeatureFlag:PublishCommentBankUpdate"
    );
    try {
      const response = await client.mutate({
        mutation: publishCommentBankMutation,
        variables: {
          input: { organizationId: organizationId, shouldUpdateComments },
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:published_successfully",
          type: "success",
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

const REDUCER_HANDLERS = {
  [SET_TEMPLATE_DATA]: (state, action) => {
    return {
      ...state,
      ...action.payload,
    };
  },
};

const initialState = {
  selectedTemplateId: null,
  mode: "VIEW",
  searchText: "",
  selectedFilter: "all",
  activeTab: "",
  first: 20,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}

export let cancelRequest;

export const generateAIResponse = ({
  type,
  context,
  entityType,
  currentCurriculumProgramId,
  action,
}) => {
  return async (dispatch, getState) => {
    try {
      let result = {};
      const response = await dispatch(
        getAIResponse({
          variables: {
            input: {
              context,
              curriculumProgramId: currentCurriculumProgramId,
              entityType,
              type,
              action,
            },
          },
          getCancelToken: token => (cancelRequest = token),
          onDownloadProgress: params => {
            const { content } = params || {};

            result = {
              isSuccess: true,
              content,
            };
          },
        })
      );

      const parsedContent = JSON.parse(result.content || "{}");
      const {
        usageMetricsId,
        userInputFlagged = false,
        temperature = "",
        model = "",
      } = response || {};

      return {
        usageMetricsId,
        userInputFlagged,
        temperature,
        model,
        voice: _.get(parsedContent, "voice", ""),
        outputStructure: _.replace(
          _.get(parsedContent, "outputStructure", ""),
          /\*/g,
          "•"
        ),
        title: _.get(parsedContent, "title", ""),
        isValid: _.get(parsedContent, "valid", "") === "false" ? false : true,
        ...result,
      };
    } catch (e) {
      return {
        isSuccess: false,
      };
    }
  };
};

export const getAllFilteredComments = ({ comments }) =>
  _.filter(comments, ({ comment }) => !_.isEmpty(comment));

export const getAllComments = ({ comments }) => {
  const filteredComments = getAllFilteredComments({ comments });

  return _.map(filteredComments, ({ comment = "" }) => comment);
};

export const createCustomAIVoiceTone = ({
  id,
  organizationId,
  curriculumProgramId,
  title,
  description,
  sampleComments,
  outputStructure,
  isDefault,
  isGlobalDefault,
  makeDefaultSelectionForAllUsers,
}) => {
  return async (dispatch, getState) => {
    const variables = { organizationId, curriculumIds: [curriculumProgramId] };
    const aiVoiceData = getAIVoiceTonesForReportsFromCache({
      ...variables,
    });
    const curriculumPrograms = _.get(aiVoiceData, "curriculumPrograms", []);
    const voiceTones = _.get(_.first(curriculumPrograms), "voiceTones", []);
    const defaultVoice = _.find(voiceTones, { isDefault: true });
    const defaultToneIndex = _.findIndex(
      voiceTones,
      tone => tone.id === defaultVoice.id
    );
    let modifiedData = {
      id: organizationId,
      curriculumPrograms: [
        {
          id: curriculumProgramId,
          voiceTones,
        },
      ],
    };

    try {
      const response = await client.mutate({
        mutation: createAIVoiceToneMutation,
        variables: {
          input: {
            title,
            description,
            sampleComments: _.map(
              sampleComments,
              ({ comment = "" }) => comment
            ),
            outputStructure,
            isDefault,
            curriculumProgramId,
            makeDefaultSelectionForAllUsers,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createAiVoiceTone: {
              isSuccess: true,
              response: [
                {
                  id,
                  title,
                  description,
                  sampleComments: _.map(sampleComments, comment => ({
                    id,
                    comment: comment,
                    __typename: "UserVoiceSampleComment",
                  })),
                  type: USER_TYPES.custom,
                  isDefault,
                  isGlobalDefault,
                  outputStructure,
                  __typename: "AiVoiceTone",
                },
              ],
            },
          },
        },
        update: (cache, { data }) => {
          const { ai } = data;
          let modifiedTones = [...voiceTones];
          const newTone = _.get(ai, "createAiVoiceTone.response[0]", []);

          // modify already existing default voice cache
          if (isDefault) {
            const modifiedPreviousDefaultVoice = {
              ...defaultVoice,
              isDefault: false,
            };
            modifiedTones = update(modifiedTones, {
              [defaultToneIndex]: {
                $set: {
                  ...modifiedPreviousDefaultVoice,
                  __typename: "AiVoiceTone",
                },
              },
            });
          }

          const modifiedTonesUpdated = update(modifiedTones, {
            $unshift: [newTone],
          });

          modifiedData = {
            id: organizationId,
            curriculumPrograms: [
              {
                id: curriculumProgramId,
                voiceTones: modifiedTonesUpdated,
              },
            ],
          };
          writeAIVoiceTonesForReportsToCache({
            variables,
            data: modifiedData,
          });
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:successfully_created_voice",
          type: "success",
          position: "toast-bottom-left",
        })
      );
      return {
        newVoice: _.get(response, "data.ai.createAiVoiceTone.response[0]", {}),
      };
    } catch (error) {
      writeAIVoiceTonesForReportsToCache({
        variables,
        data: modifiedData,
      });
      dispatchError({ dispatch, error });
    }
  };
};

export const updateCustomAIVoiceTone = ({
  organizationId,
  curriculumProgramId,
  id,
  title,
  description,
  sampleComments,
  type,
  outputStructure,
  isDefault,
  isGlobalDefault,
  makeDefaultSelectionForAllUsers,
}) => {
  return async (dispatch, getState) => {
    const variables = { organizationId, curriculumIds: [curriculumProgramId] };
    const aiVoiceData = getAIVoiceTonesForReportsFromCache({
      ...variables,
    });
    const curriculumPrograms = _.get(aiVoiceData, "curriculumPrograms", []);
    const voiceTones = _.get(_.first(curriculumPrograms), "voiceTones", []);
    const defaultVoice = _.find(voiceTones, { isDefault: true });
    const defaultToneIndex = _.findIndex(
      voiceTones,
      tone => tone.id === defaultVoice.id
    );

    let modifiedTones = [...voiceTones];

    const toneIndex = _.findIndex(modifiedTones, tone => tone.id === id);

    let modifiedData = {
      id: organizationId,
      curriculumPrograms: [
        {
          id: curriculumProgramId,
          voiceTones: modifiedTones,
        },
      ],
    };

    try {
      const response = await client.mutate({
        mutation: updateAIVoiceToneMutation,
        variables: {
          input: {
            id,
            title,
            description,
            sampleComments: _.map(
              sampleComments,
              ({ comment = "" }) => comment
            ),
            outputStructure,
            isDefault,
            makeDefaultSelectionForAllUsers,
            curriculumProgramId,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            updateAiVoiceTone: {
              isSuccess: true,
              response: [
                {
                  id,
                  title,
                  description,
                  sampleComments,
                  type,
                  outputStructure,
                  isDefault,
                  isGlobalDefault,
                  __typename: "AiVoiceTone",
                },
              ],
            },
          },
        },
        update: (cache, { data }) => {
          const { ai } = data;
          const updatedTone = _.get(ai, "updateAiVoiceTone.response[0]", {});
          // modify already existing default voice cache
          if (isDefault) {
            const modifiedPreviousDefaultVoice = {
              ...defaultVoice,
              isDefault: false,
            };
            modifiedTones = update(modifiedTones, {
              [defaultToneIndex]: {
                $set: {
                  ...modifiedPreviousDefaultVoice,
                  __typename: "AiVoiceTone",
                },
              },
            });
          }

          const updatedModifiedTones = update(modifiedTones, {
            [toneIndex]: {
              $set: {
                ...updatedTone,
                __typename: "AiVoiceTone",
              },
            },
          });
          modifiedData = {
            id: organizationId,
            curriculumPrograms: [
              {
                id: curriculumProgramId,
                voiceTones: updatedModifiedTones,
              },
            ],
          };
          writeAIVoiceTonesForReportsToCache({
            variables,
            data: modifiedData,
          });
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:successfully_updated_voice",
          type: "success",
          position: "toast-bottom-left",
        })
      );
      return {
        updatedVoice: _.get(
          response,
          "data.ai.updateAiVoiceTone.response[0]",
          {}
        ),
      };
    } catch (error) {
      writeAIVoiceTonesForReportsToCache({
        variables,
        data: modifiedData,
      });
      dispatchError({ dispatch, error });
    }
  };
};

export const deleteCustomAIVoiceTone = ({
  organizationId,
  ids,
  curriculumProgramId,
}) => {
  return async (dispatch, getState) => {
    const variables = { organizationId, curriculumIds: [curriculumProgramId] };
    const aiVoiceData = getAIVoiceTonesForReportsFromCache({
      ...variables,
    });
    const curriculumPrograms = _.get(aiVoiceData, "curriculumPrograms", []);
    const voiceTones = _.get(_.first(curriculumPrograms), "voiceTones", []);
    const deletedVoice = _.find(voiceTones, { id: ids[0] });
    let modifiedData = {
      id: organizationId,
      curriculumPrograms: [
        {
          id: curriculumProgramId,
          voiceTones,
        },
      ],
    };

    try {
      await client.mutate({
        mutation: deleteAIVoiceToneMutation,
        variables: {
          input: {
            ids,
            curriculumProgramId,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            deleteAiVoiceTone: true,
          },
        },
        update: (cache, { data }) => {
          const { ai } = data;
          const deletedTone = _.get(ai, "deleteAiVoiceTone", false);
          let modifiedTones = [...voiceTones];
          if (deletedTone) {
            if (_.get(deletedVoice, "isDefault", false)) {
              const globalDefaultVoice = _.find(voiceTones, {
                isGlobalDefault: true,
              });
              const globalDefaultToneIndex = _.findIndex(
                voiceTones,
                tone => tone.id === globalDefaultVoice.id
              );
              const modifiedDefaultVoice = {
                ...globalDefaultVoice,
                isDefault: true,
              };
              modifiedTones[globalDefaultToneIndex] = modifiedDefaultVoice;
            }
            modifiedTones = _.filter(modifiedTones, tone => tone.id !== ids[0]);
            setTimeout(() => {
              modifiedData = {
                id: organizationId,
                curriculumPrograms: [
                  {
                    id: curriculumProgramId,
                    voiceTones: modifiedTones,
                  },
                ],
              };
              writeAIVoiceTonesForReportsToCache({
                variables,
                data: modifiedData,
              });
            });
          }
        },
      });
      dispatch(
        setToastMsg({
          msg: "toastMsgs:successfully_deleted_voice",
          type: "success",
          position: "toast-bottom-left",
        })
      );
      return {
        deletedVoice: _.find(voiceTones, { id: ids[0] }),
      };
    } catch (error) {
      writeAIVoiceTonesForReportsToCache({
        variables,
        data: modifiedData,
      });
      dispatchError({ dispatch, error });
    }
  };
};
